import { supabase } from '../lib/supabase';
import { PostgrestError } from '@supabase/supabase-js';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../hooks/useDomainQuery';

// ============================================================================
// INTERFACES TYPESCRIPT
// ============================================================================

export interface ElearningCustomerProfile {
  id: string;
  tenant_id: string;
  domain: string | null;
  email: string;
  full_name: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  postal_code: string | null;
  preferences: Record<string, any>;
  access_token: string | null;
  token_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ElearningOrder {
  id: string;
  tenant_id: string;
  domain: string | null;
  customer_profile_id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  total_amount: number;
  currency: string;
  status: 'pending_payment' | 'completed' | 'cancelled' | 'refunded';
  payment_method: string | null;
  payment_reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ElearningOrderItem {
  id: string;
  order_id: string;
  course_id: string;
  price_at_purchase: number;
  currency: string;
  domain: string | null;
  created_at: string;
}

export interface ElearningEnrollment {
  id: string;
  tenant_id: string;
  domain: string | null;
  student_id: string;
  course_id: string;
  order_id: string | null;
  enrolled_at: string;
  completed_at: string | null;
  status: 'active' | 'completed' | 'suspended' | 'cancelled';
  progress_percentage: number;
  last_accessed_at: string | null;
  certificate_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface ElearningCartItem {
  course_id: string;
  quantity: number; // Toujours 1 pour les cours
  price: number;
  title: string;
  thumbnail_url?: string;
  instructor_name?: string;
  duration_hours?: number;
}

export interface ElearningCustomerInfo {
  full_name: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  preferences?: Record<string, any>;
}

// ============================================================================
// FONCTIONS DE GESTION DES PROFILS CLIENTS
// ============================================================================

/**
 * Crée ou met à jour un profil client e-learning
 */
export async function createOrUpdateCustomerProfile(
  tenantId: string,
  customerInfo: ElearningCustomerInfo
): Promise<ElearningCustomerProfile> {
  try {
    const { domain } = getDomainFilterParams();
    
    // Vérifier si le profil existe déjà
    let profileQuery = supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('email', customerInfo.email);
    
    profileQuery = applyDomainFilterToQuery(profileQuery);
    const { data: existingProfile, error: findError } = await profileQuery.single();

    if (findError && findError.code !== 'PGRST116') {
      throw findError;
    }

    if (existingProfile) {
      // Mettre à jour le profil existant
      const { data: updatedProfile, error: updateError } = await supabase
        .from('elearn_customer_profiles')
        .update({
          full_name: customerInfo.full_name,
          phone: customerInfo.phone,
          address: customerInfo.address,
          city: customerInfo.city,
          country: customerInfo.country,
          postal_code: customerInfo.postal_code,
          preferences: customerInfo.preferences || {},
          updated_at: new Date().toISOString()
        })
        .eq('id', existingProfile.id)
        .select()
        .single();

      if (updateError) throw updateError;
      return updatedProfile as ElearningCustomerProfile;
    } else {
      // Créer un nouveau profil
      const { data: newProfile, error: createError } = await supabase
        .from('elearn_customer_profiles')
        .insert({
          tenant_id: tenantId,
          domain: domain,
          email: customerInfo.email,
          full_name: customerInfo.full_name,
          phone: customerInfo.phone,
          address: customerInfo.address,
          city: customerInfo.city,
          country: customerInfo.country,
          postal_code: customerInfo.postal_code,
          preferences: customerInfo.preferences || {}
        })
        .select()
        .single();

      if (createError) throw createError;
      return newProfile as ElearningCustomerProfile;
    }
  } catch (error) {
    console.error('Erreur lors de la création/mise à jour du profil client:', error);
    throw error;
  }
}

/**
 * Récupère un profil client par email
 */
export async function getCustomerProfileByEmail(
  tenantId: string,
  email: string
): Promise<ElearningCustomerProfile | null> {
  try {
    let query = supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('email', email);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data as ElearningCustomerProfile | null;
  } catch (error) {
    console.error('Erreur lors de la récupération du profil client:', error);
    throw error;
  }
}

// ============================================================================
// FONCTIONS DE GESTION DES COMMANDES
// ============================================================================

/**
 * Crée une commande e-learning complète avec inscription automatique
 * Cette fonction encapsule toute la logique de checkout
 * @param markAsPaid Si true, marque la commande comme payée et crée les inscriptions. Sinon, crée la commande en attente de paiement.
 */
export async function createElearningOrder(
  tenantId: string,
  cartItems: ElearningCartItem[],
  customerInfo: ElearningCustomerInfo,
  userId?: string,
  markAsPaid: boolean = true
): Promise<{ order: ElearningOrder; enrollments: ElearningEnrollment[] }> {
  try {
    // Étape 1: Créer ou mettre à jour le profil client
    const customerProfile = await createOrUpdateCustomerProfile(tenantId, customerInfo);

    // Étape 2: Calculer le montant total
    const totalAmount = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    // Étape 3: Générer un numéro de commande unique
    const orderNumber = await generateOrderNumber(tenantId);

    // Étape 4: Créer la commande
    const { domain } = getDomainFilterParams();
    const { data: order, error: orderError } = await supabase
      .from('elearn_orders')
      .insert({
        tenant_id: tenantId,
        domain: domain,
        customer_profile_id: customerProfile.id,
        order_number: orderNumber,
        customer_name: customerInfo.full_name,
        customer_email: customerInfo.email,
        total_amount: totalAmount,
        currency: 'FCFA',
        status: markAsPaid ? 'completed' : 'pending_payment', // Si markAsPaid est false, la commande est en attente de paiement
        payment_method: markAsPaid ? 'simulation' : null,
        payment_reference: markAsPaid ? `SIM-${Date.now()}` : null,
        notes: markAsPaid ? 'Commande créée via simulation de paiement' : 'Commande en attente de paiement'
      })
      .select()
      .single();

    if (orderError) throw orderError;

    // Étape 5: Créer les articles de commande
    const orderItems = cartItems.map(item => ({
      order_id: order.id,
      course_id: item.course_id,
      price_at_purchase: item.price,
      currency: 'FCFA',
      domain: domain
    }));

    const { error: itemsError } = await supabase
      .from('elearn_order_items')
      .insert(orderItems);

    if (itemsError) throw itemsError;

    // Étape 6: Créer les inscriptions (accès aux cours) uniquement si la commande est marquée comme payée
    const enrollments: ElearningEnrollment[] = [];
    
    if (markAsPaid) {
      for (const item of cartItems) {
        // Si l'utilisateur est connecté, vérifier s'il n'est pas déjà inscrit
        if (userId) {
          let enrollmentQuery = supabase
            .from('elearn_enrollments')
            .select('id')
            .eq('student_id', userId)
            .eq('course_id', item.course_id);
          
          enrollmentQuery = applyDomainFilterToQuery(enrollmentQuery);
          const { data: existingEnrollment } = await enrollmentQuery.single();

          if (!existingEnrollment) {
            const { data: enrollment, error: enrollmentError } = await supabase
              .from('elearn_enrollments')
              .insert({
                tenant_id: tenantId,
                domain: domain,
                student_id: userId,
                course_id: item.course_id,
                order_id: order.id,
                status: 'active',
                progress_percentage: 0
              })
              .select()
              .single();

            if (enrollmentError) throw enrollmentError;
            enrollments.push(enrollment as ElearningEnrollment);
          }
        } else {
          // Si l'utilisateur n'est pas connecté, créer une inscription liée au profil client
          const { data: enrollment, error: enrollmentError } = await supabase
            .from('elearn_enrollments')
            .insert({
              tenant_id: tenantId,
              domain: domain,
              student_id: customerProfile.id, // Utiliser l'ID du profil client comme ID étudiant
              course_id: item.course_id,
              order_id: order.id,
              status: 'active',
              progress_percentage: 0
            })
            .select()
            .single();

          if (enrollmentError) throw enrollmentError;
          enrollments.push(enrollment as ElearningEnrollment);
        }
      }
    }

    return {
      order: order as ElearningOrder,
      enrollments
    };
  } catch (error) {
    console.error('Erreur lors de la création de la commande e-learning:', error);
    throw error;
  }
}

/**
 * Récupère une commande par son numéro
 */
export async function getOrderByNumber(
  tenantId: string,
  orderNumber: string
): Promise<ElearningOrder | null> {
  try {
    let query = supabase
      .from('elearn_orders')
      .select(`
        *,
        customer_profile:customer_profile_id(*),
        items:elearn_order_items(
          *,
          course:course_id(id, title, slug, thumbnail_url, instructor_id)
        )
      `)
      .eq('order_number', orderNumber);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data as ElearningOrder | null;
  } catch (error) {
    console.error('Erreur lors de la récupération de la commande:', error);
    throw error;
  }
}

/**
 * Récupère toutes les commandes d'un client
 */
export async function getCustomerOrders(
  tenantId: string,
  customerEmail: string,
  limit: number = 10,
  offset: number = 0
) {
  try {
    let query = supabase
      .from('elearn_orders')
      .select(`
        *,
        items:elearn_order_items(
          *,
          course:course_id(id, title, slug, thumbnail_url, instructor_id)
        )
      `)
      .eq('customer_email', customerEmail)
      .order('created_at', { ascending: false })
      .limit(limit)
      .range(offset, offset + limit - 1);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des commandes client:', error);
    throw error;
  }
}

// ============================================================================
// FONCTIONS DE GESTION DES INSCRIPTIONS
// ============================================================================

/**
 * Vérifie si un utilisateur est inscrit à un cours spécifique
 */
export async function isUserEnrolledInCourse(
  tenantId: string,
  userId: string,
  courseId: string
): Promise<boolean> {
  try {
    let query = supabase
      .from('elearn_enrollments')
      .select('id')
      .eq('student_id', userId)
      .eq('course_id', courseId)
      .eq('status', 'active');
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    
    return !!data; // Retourne true si l'inscription existe, false sinon
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'inscription au cours:', error);
    return false; // En cas d'erreur, on considère que l'utilisateur n'est pas inscrit
  }
}

/**
 * Récupère toutes les inscriptions d'un utilisateur
 */
export async function getUserEnrollments(
  tenantId: string,
  userId: string,
  status?: 'active' | 'completed' | 'suspended' | 'cancelled'
) {
  try {
    let query = supabase
      .from('elearn_enrollments')
      .select(`
        *,
        course:course_id(
          id, title, slug, description, thumbnail_url, 
          difficulty_level, duration_hours, instructor_id,
          instructor:instructor_id(name, avatar_url)
        )
      `)
      .eq('student_id', userId)
      .order('enrolled_at', { ascending: false });
    
    query = applyDomainFilterToQuery(query);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des inscriptions:', error);
    throw error;
  }
}

/**
 * Récupère une inscription spécifique
 */
export async function getEnrollmentById(
  tenantId: string,
  enrollmentId: string,
  userId: string
) {
  try {
    let query = supabase
      .from('elearn_enrollments')
      .select(`
        *,
        course:course_id(
          *,
          instructor:instructor_id(*),
          modules:elearn_modules(
            *,
            lessons:elearn_lessons(*)
          )
        )
      `)
      .eq('id', enrollmentId)
      .eq('student_id', userId);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error) throw error;

    // Trier les modules et leçons
    if (data.course?.modules) {
      data.course.modules.sort((a: any, b: any) => a.sort_order - b.sort_order);
      data.course.modules.forEach((module: any) => {
        if (module.lessons) {
          module.lessons.sort((a: any, b: any) => a.sort_order - b.sort_order);
        }
      });
    }

    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'inscription:', error);
    throw error;
  }
}

/**
 * Met à jour la progression d'une inscription
 */
export async function updateEnrollmentProgress(
  enrollmentId: string,
  progressPercentage: number,
  userId: string
) {
  try {
    const updateData: any = {
      progress_percentage: Math.min(100, Math.max(0, progressPercentage)),
      last_accessed_at: new Date().toISOString()
    };

    // Si le cours est complété (100%), marquer comme terminé
    if (progressPercentage >= 100) {
      updateData.status = 'completed';
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('elearn_enrollments')
      .update(updateData)
      .eq('id', enrollmentId)
      .eq('student_id', userId)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la progression:', error);
    throw error;
  }
}

// ============================================================================
// FONCTIONS DE GESTION DE LA PROGRESSION DES LEÇONS
// ============================================================================

/**
 * Récupère la progression des leçons pour une inscription donnée
 */
export async function getLessonProgressForEnrollment(enrollmentId: string) {
  try {
    console.log('getLessonProgressForEnrollment - Début de la récupération pour l\'inscription:', enrollmentId);
    
    const { data, error } = await supabase
      .from('elearn_lesson_progress')
      .select('*')
      .eq('enrollment_id', enrollmentId);
    
    if (error) {
      console.error('getLessonProgressForEnrollment - Erreur:', error);
      throw error;
    }
    
    // Transformer les données en un objet avec les ID de leçon comme clés et les pourcentages comme valeurs
    const progressMap: Record<string, number> = {};
    
    if (data && data.length > 0) {
      console.log(`getLessonProgressForEnrollment - ${data.length} progressions trouvées`);
      data.forEach((item: any) => {
        progressMap[item.lesson_id] = item.completion_percentage || 0;
      });
    } else {
      console.log('getLessonProgressForEnrollment - Aucune progression trouvée');
    }
    
    return progressMap;
  } catch (error) {
    console.error('Erreur lors de la récupération de la progression des leçons:', error);
    return {}; // Retourner un objet vide en cas d'erreur
  }
}

/**
 * Marque une leçon comme terminée
 */
export async function markLessonAsCompleted(
  enrollmentId: string,
  lessonId: string,
  timeSpentSeconds: number = 0
) {
  try {
    const { data, error } = await supabase
      .from('elearn_lesson_progress')
      .upsert({
        enrollment_id: enrollmentId,
        lesson_id: lessonId,
        is_completed: true,
        completion_percentage: 100,
        time_spent_seconds: timeSpentSeconds,
        completed_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;

    // Recalculer la progression globale du cours
    await recalculateEnrollmentProgress(enrollmentId);

    return data;
  } catch (error) {
    console.error('Erreur lors du marquage de la leçon comme terminée:', error);
    throw error;
  }
}

/**
 * Met à jour la progression d'une leçon
 */
export async function updateLessonProgress(
  enrollmentId: string,
  lessonId: string,
  completionPercentage: number,
  timeSpentSeconds: number = 0
) {
  try {
    console.log('updateLessonProgress - Début de la mise à jour', { enrollmentId, lessonId, completionPercentage });
    
    // Vérifier d'abord si une entrée existe déjà
    const { data: existingProgress, error: checkError } = await supabase
      .from('elearn_lesson_progress')
      .select('*')
      .eq('enrollment_id', enrollmentId)
      .eq('lesson_id', lessonId)
      .maybeSingle();
      
    if (checkError) {
      console.error('Erreur lors de la vérification de la progression existante:', checkError);
      throw checkError;
    }
    
    let data;
    let error;
    
    const progressData = {
      enrollment_id: enrollmentId,
      lesson_id: lessonId,
      is_completed: completionPercentage >= 100,
      completion_percentage: Math.min(100, Math.max(0, completionPercentage)),
      time_spent_seconds: existingProgress ? (existingProgress.time_spent_seconds || 0) + timeSpentSeconds : timeSpentSeconds,
      completed_at: completionPercentage >= 100 ? new Date().toISOString() : null
    };
    
    if (existingProgress) {
      console.log('updateLessonProgress - Mise à jour d\'une progression existante');
      // Mettre à jour l'entrée existante
      const result = await supabase
        .from('elearn_lesson_progress')
        .update(progressData)
        .eq('enrollment_id', enrollmentId)
        .eq('lesson_id', lessonId)
        .select()
        .single();
        
      data = result.data;
      error = result.error;
    } else {
      console.log('updateLessonProgress - Création d\'une nouvelle progression');
      // Créer une nouvelle entrée
      const result = await supabase
        .from('elearn_lesson_progress')
        .insert(progressData)
        .select()
        .single();
        
      data = result.data;
      error = result.error;
    }

    if (error) throw error;

    // Recalculer la progression globale du cours
    await recalculateEnrollmentProgress(enrollmentId);

    return data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la progression de la leçon:', error);
    throw error;
  }
}

/**
 * Récupère la progression d'un utilisateur pour un cours
 */
export async function getEnrollmentProgress(enrollmentId: string) {
  try {
    const { data, error } = await supabase
      .from('elearn_lesson_progress')
      .select(`
        *,
        lesson:lesson_id(id, title, module_id, sort_order)
      `)
      .eq('enrollment_id', enrollmentId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération de la progression:', error);
    throw error;
  }
}

// ============================================================================
// FONCTIONS UTILITAIRES PRIVÉES
// ============================================================================

/**
 * Génère un numéro de commande unique
 */
async function generateOrderNumber(tenantId: string): Promise<string> {
  try {
    const { data, error } = await supabase
      .rpc('generate_elearn_order_number', { tenant_uuid: tenantId });

    if (error) throw error;

    return data;
  } catch (error) {
    // Fallback si la fonction RPC n'est pas disponible
    console.warn('Fonction RPC non disponible, génération manuelle du numéro de commande');
    const year = new Date().getFullYear();
    const timestamp = Date.now().toString().slice(-6);
    return `EL-${year}-${timestamp}`;
  }
}

/**
 * Recalcule la progression globale d'une inscription
 */
async function recalculateEnrollmentProgress(enrollmentId: string) {
  try {
    // Récupérer toutes les leçons du cours et leur progression
    const { data: enrollment, error: enrollmentError } = await supabase
      .from('elearn_enrollments')
      .select(`
        id,
        course:course_id(
          modules:elearn_modules(
            lessons:elearn_lessons(id)
          )
        )
      `)
      .eq('id', enrollmentId)
      .single();

    if (enrollmentError) throw enrollmentError;

    // Compter le nombre total de leçons
    let totalLessons = 0;
    if (enrollment.course?.modules) {
      enrollment.course.modules.forEach((module: any) => {
        if (module.lessons) {
          totalLessons += module.lessons.length;
        }
      });
    }

    if (totalLessons === 0) return;

    // Compter les leçons terminées
    const { data: completedLessons, error: progressError } = await supabase
      .from('elearn_lesson_progress')
      .select('id')
      .eq('enrollment_id', enrollmentId)
      .eq('is_completed', true);

    if (progressError) throw progressError;

    const completedCount = completedLessons?.length || 0;
    const progressPercentage = Math.round((completedCount / totalLessons) * 100);

    // Mettre à jour la progression de l'inscription
    const { error: updateError } = await supabase
      .from('elearn_enrollments')
      .update({
        progress_percentage: progressPercentage,
        status: progressPercentage >= 100 ? 'completed' : 'active',
        completed_at: progressPercentage >= 100 ? new Date().toISOString() : null
      })
      .eq('id', enrollmentId);

    if (updateError) throw updateError;
  } catch (error) {
    console.error('Erreur lors du recalcul de la progression:', error);
    // Ne pas propager l'erreur pour ne pas bloquer les autres opérations
  }
}

// ============================================================================
// FONCTIONS DE STATISTIQUES
// ============================================================================

/**
 * Récupère les statistiques des commandes
 */
export async function getOrdersStats(tenantId: string) {
  try {
    let ordersQuery = supabase
      .from('elearn_orders')
      .select('id, total_amount', { count: 'exact' })
      .eq('status', 'completed');
    
    ordersQuery = applyDomainFilterToQuery(ordersQuery);
    const { data: totalOrders, error: ordersError } = await ordersQuery;

    if (ordersError) throw ordersError;

    const totalRevenue = totalOrders?.reduce((sum, order) => sum + order.total_amount, 0) || 0;

    return {
      totalOrders: totalOrders?.length || 0,
      totalRevenue
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques des commandes:', error);
    throw error;
  }
}

// Fonction pour obtenir les détails d'une inscription avec modules et leçons
export async function getEnrollmentDetails(tenantId: number, enrollmentId: string) {
  const { data, error } = await supabase
    .from('elearn_enrollments')
    .select(`
      id,
      status,
      progress_percentage,
      enrolled_at,
      completed_at,
      certificate_url,
      course:elearn_courses!inner (
        id,
        title,
        description,
        thumbnail_url,
        duration_hours,
        level,
        price,
        instructor:elearn_instructors!inner (
          id,
          name,
          avatar_url,
          bio
        ),
        modules:elearn_modules!inner (
          id,
          title,
          description,
          order_index,
          lessons:elearn_lessons!inner (
            id,
            title,
            description,
            content_type,
            content_url,
            duration_seconds,
            order_index,
            resources_url
          )
        )
      )
    `)
    .eq('tenant_id', tenantId)
    .eq('id', enrollmentId)
    .single();

  if (error) {
    console.error('Erreur lors de la récupération des détails:', error);
    throw error;
  }

  // Trier les modules et leçons par ordre
  if (data?.course?.modules) {
    data.course.modules.sort((a: any, b: any) => a.order_index - b.order_index);
    data.course.modules.forEach((module: any) => {
      if (module.lessons) {
        module.lessons.sort((a: any, b: any) => a.order_index - b.order_index);
      }
    });
  }

  return data;
}

// Fonction pour obtenir la progression d'un utilisateur pour un cours
export async function getUserCourseProgress(tenantId: number, userId: string, courseId: string) {
  const { data, error } = await supabase
    .from('elearn_lesson_progress')
    .select(`
      id,
      lesson_id,
      progress_percentage,
      completed_at,
      lesson:elearn_lessons!inner (
        id,
        title,
        module:elearn_modules!inner (
          id,
          course_id
        )
      )
    `)
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .eq('lesson.module.course_id', courseId);

  if (error) {
    console.error('Erreur lors de la récupération de la progression:', error);
    throw error;
  }

  return data || [];
}

// Fonction pour obtenir les statistiques d'un utilisateur
export async function getUserStats(tenantId: number, userId: string) {
  const { data, error } = await supabase
    .from('elearn_enrollments')
    .select(`
      id,
      status,
      progress_percentage,
      enrolled_at,
      completed_at
    `)
    .eq('tenant_id', tenantId)
    .eq('user_id', userId);

  if (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    throw error;
  }

  const stats = {
    totalCourses: data.length,
    activeCourses: data.filter(e => e.status === 'active').length,
    completedCourses: data.filter(e => e.status === 'completed').length,
    averageProgress: data.length > 0 
      ? Math.round(data.reduce((sum, e) => sum + e.progress_percentage, 0) / data.length)
      : 0,
    totalHoursLearned: data.filter(e => e.completed_at).length * 2 // Simulation
  };

  return stats;
}
